'use client'

import React, { useState, useEffect } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { useQuery } from '@apollo/client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ChevronRight, ChevronDown, Plus } from 'lucide-react'
import { GET_ATTRIBUTES_TREE } from './productAttributeQueries'
import ProductAttributeGroupForm from '../ProductAttributeGroups/components/ProductAttributeGroupForm'
import ProductAttributeForm from './components/ProductAttributeForm'
import useOpenDrawer from '@/hooks/useOpenDrawer'
import { PageContent } from '@/components/shared/page-content'
import { useLanguage } from '@/context/LanguageContext'
import { Skeleton } from '@/components/ui/skeleton'

interface AttributeProfile {
  id: number
  name: string
  description?: string
  language: string
}

interface AttributeRow {
  id: number
  code?: string
  product_attribute_profiles: AttributeProfile[]
  product_containers: { id: number }[]
}

interface GroupRow {
  id: number
  code?: string
  product_attribute_group_profiles: AttributeProfile[]
  product_attributes: AttributeRow[]
}

export default function ProductAttributes() {
  const { language: currentLanguage } = useLanguage()
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())

  const { data, loading, error } = useQuery(GET_ATTRIBUTES_TREE, {
    fetchPolicy: 'cache-and-network',
  })

  const groups: GroupRow[] = data?.findManyProductAttributeGroup || []

  // Expand all groups by default when data loads
  useEffect(() => {
    if (groups.length > 0 && expandedGroups.size === 0) {
      setExpandedGroups(new Set(groups.map(g => g.id)))
    }
  }, [groups])

  const openGroupForm = useOpenDrawer(ProductAttributeGroupForm)
  const openAttributeForm = useOpenDrawer(ProductAttributeForm)

  const toggleGroup = (groupId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupId)) {
        next.delete(groupId)
      } else {
        next.add(groupId)
      }
      return next
    })
  }

  const getName = (profiles: AttributeProfile[]) => {
    return (
      profiles.find(
        p => p.language.toLowerCase() === currentLanguage.toLowerCase()
      )?.name ||
      profiles[0]?.name ||
      ''
    )
  }

  const handleAddAttribute = (groupId: number, e: React.MouseEvent) => {
    e.stopPropagation()
    openAttributeForm({ id: null, defaultGroupId: groupId })
  }

  return (
    <PageContent>
      <div className="flex justify-between items-center">
        <HeaderSection title="Product Attributes" />
        <div className="flex gap-2">
          <Button variant="outline" onClick={openGroupForm}>
            New Group +
          </Button>
          <Button onClick={openAttributeForm}>New Attribute +</Button>
        </div>
      </div>

      <div className="rounded-md border">
        {/* Table header */}
        <div className="grid grid-cols-[40px_1fr_150px_120px_80px] gap-2 px-4 py-3 bg-muted/50 text-xs font-medium text-muted-foreground border-b">
          <div></div>
          <div>Name</div>
          <div>Code</div>
          <div>Type</div>
          <div className="text-right">Items</div>
        </div>

        {/* Table body */}
        {loading ? (
          <div className="p-4 space-y-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No attribute groups yet. Create a group to get started.
          </div>
        ) : (
          <div>
            {groups.map(group => {
              const isExpanded = expandedGroups.has(group.id)
              return (
                <React.Fragment key={`group-${group.id}`}>
                  {/* Group row */}
                  <div
                    className="grid grid-cols-[40px_1fr_150px_120px_80px] gap-2 px-4 py-3 border-b hover:bg-muted/30 cursor-pointer items-center"
                    onClick={() => openGroupForm({ id: group.id })}
                  >
                    <button
                      onClick={e => toggleGroup(group.id, e)}
                      className="flex items-center justify-center h-6 w-6 rounded hover:bg-muted"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    <div className="font-medium text-sm">
                      {getName(group.product_attribute_group_profiles)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {group.code || '-'}
                    </div>
                    <Badge variant="outline" className="w-fit text-xs">
                      Group
                    </Badge>
                    <div className="text-right">
                      <Badge variant="secondary" className="text-xs">
                        {group.product_attributes.length}
                      </Badge>
                    </div>
                  </div>

                  {/* Attribute rows (children) */}
                  {isExpanded &&
                    group.product_attributes.map(attr => (
                      <div
                        key={`attr-${attr.id}`}
                        className="grid grid-cols-[40px_1fr_150px_120px_80px] gap-2 px-4 py-2.5 border-b hover:bg-muted/20 cursor-pointer items-center bg-muted/5"
                        onClick={() => openAttributeForm({ id: attr.id })}
                      >
                        <div></div>
                        <div className="text-sm pl-4">
                          {getName(attr.product_attribute_profiles)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {attr.code || '-'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Attribute
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary" className="text-xs">
                            {attr.product_containers.length}
                          </Badge>
                        </div>
                      </div>
                    ))}

                  {/* Add attribute button inside expanded group */}
                  {isExpanded && (
                    <div
                      className="grid grid-cols-[40px_1fr] gap-2 px-4 py-2 border-b bg-muted/5"
                    >
                      <div></div>
                      <button
                        onClick={e => handleAddAttribute(group.id, e)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground pl-4"
                      >
                        <Plus className="h-3 w-3" />
                        Add attribute
                      </button>
                    </div>
                  )}
                </React.Fragment>
              )
            })}
          </div>
        )}
      </div>
    </PageContent>
  )
}
